import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { BankingPartnersList } from '@/features/banking-partners/components/BankingPartnersList';
import { BankingPartnerForm } from '@/features/banking-partners/components/BankingPartnerForm';
import { BankingPartnerDetail } from '@/features/banking-partners/components/BankingPartnerDetail';
import { useAuth } from '@/components/AuthProvider';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { bankingPartnerService } from '@/features/banking-partners/services/bankingPartnerService';

function EditPartner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: partner, isLoading } = useQuery({
    queryKey: ['bankingPartner', id],
    queryFn: () => bankingPartnerService.getBankingPartner(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Banking partner not found</p>
        <Button className="mt-4" onClick={() => navigate('/app/banking-partners')}>
          Back to Partners
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/app/banking-partners/${id}`)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Edit Banking Partner</h1>
          <p className="text-muted-foreground">
            Update banking partner information
          </p>
        </div>
      </div>
      <Card className="p-6">
        <BankingPartnerForm
          initialData={partner}
          onSuccess={() => navigate(`/app/banking-partners/${id}`)}
        />
      </Card>
    </div>
  );
}

export function BankingPartnersManagement() {
  const { isSuperAdmin, loading } = useAuth();
  const navigate = useNavigate();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Only super admins can access banking partners
  if (!isSuperAdmin) {
    return <Navigate to="/app" replace />;
  }

  return (
    <Routes>
      <Route path="/" element={<BankingPartnersList />} />
      <Route
        path="/new"
        element={
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/app/banking-partners')}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold tracking-tight">New Banking Partner</h1>
                <p className="text-muted-foreground">
                  Create a new banking partner relationship
                </p>
              </div>
            </div>
            <Card className="p-6">
              <BankingPartnerForm />
            </Card>
          </div>
        }
      />
      <Route path="/:id/edit" element={<EditPartner />} />
      <Route path="/:id" element={<BankingPartnerDetail />} />
    </Routes>
  );
}
